/**
 * CR168: Flyer Bucket Sources Page
 * Admin page for managing flyer bucket configurations
 */

import { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, RefreshCw, Pencil, Trash2, FolderOpen, TestTube } from "lucide-react";
import { useFlyerBucketSources } from "@/hooks/useFlyerBucketSources";
import { useFlyerBucketDiscovery } from "@/hooks/useFlyerBucketDiscovery";
import { BucketSourceEditorDialog } from "@/components/admin/flyer-tools/BucketSourceEditorDialog";
import { DiscoveryResultsDialog } from "@/components/admin/flyer-tools/DiscoveryResultsDialog";
import { S05ConfigBadge } from "@/components/admin/flyer-tools/S05StatusBadge";
import type { FlyerBucketSource, DiscoveryResult } from "@/lib/flyer-bucket/types";

export default function FlyerBucketSources() {
  const { sources, loading, fetchSources, createSource, updateSource, deleteSource } = useFlyerBucketSources();
  const { discovering, runDiscovery } = useFlyerBucketDiscovery();

  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState<FlyerBucketSource | null>(null);
  const [discoveryResult, setDiscoveryResult] = useState<DiscoveryResult | null>(null);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [testingSourceId, setTestingSourceId] = useState<string | null>(null);

  const handleEdit = (source: FlyerBucketSource) => {
    setSelectedSource(source);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setSelectedSource(null);
    setEditorOpen(true);
  };

  const handleTestDiscovery = async (sourceId: string) => {
    setTestingSourceId(sourceId);
    const result = await runDiscovery(sourceId);
    setTestingSourceId(null);
    if (result) {
      setDiscoveryResult(result);
      setDiscoveryOpen(true);
    }
  };

  const handleSave = async (data: Omit<FlyerBucketSource, "id" | "created_at" | "updated_at">) => {
    if (selectedSource) {
      return await updateSource(selectedSource.id, data);
    } else {
      const id = await createSource(data);
      return id !== null;
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm("Are you sure you want to delete this bucket source?")) {
      await deleteSource(id);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Flyer Bucket Sources</h1>
            <p className="text-muted-foreground">
              Configure storage buckets for automated flyer ingestion
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchSources} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Add Bucket Source
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Configured Sources</CardTitle>
            <CardDescription>
              Storage buckets that will be scanned for flyer PDFs during scheduled runs
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : sources.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No bucket sources configured yet.</p>
                <p className="text-sm mt-1">Add a bucket source to enable automated flyer discovery.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sources.map((source) => (
                  <div
                    key={source.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{source.name}</span>
                        <Badge variant={source.is_enabled ? "default" : "secondary"}>
                          {source.is_enabled ? "Enabled" : "Disabled"}
                        </Badge>
                        <S05ConfigBadge
                          enabled={source.s0_flyer_fetch_enabled}
                          mode={source.s0_flyer_fetch_mode}
                        />
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Bucket: {source.bucket_name}</span>
                        {source.prefix && <span>Prefix: {source.prefix}</span>}
                        <span>Lookahead: {source.lookahead_days} days</span>
                        {source.allow_future_flyers && (
                          <Badge variant="outline" className="text-xs">Future allowed</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTestDiscovery(source.id)}
                        disabled={discovering && testingSourceId === source.id}
                      >
                        {discovering && testingSourceId === source.id ? (
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <TestTube className="h-4 w-4 mr-2" />
                        )}
                        Test Discovery
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(source)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(source.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <BucketSourceEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        source={selectedSource}
        onSave={handleSave}
      />

      <DiscoveryResultsDialog
        open={discoveryOpen}
        onOpenChange={setDiscoveryOpen}
        result={discoveryResult}
      />
    </AdminLayout>
  );
}
